function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import { jsx, ParamComponent } from 'panel';
import isEqual from 'lodash/isEqual';
import { IronContextRegistry } from 'common/report/context-registry';
import { SegmentFilterController, SelectMode } from 'common/charts/contexts/segment-filter-controller';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { DisplayOptions, InsightsBookmarkSections } from 'common/report/insights/util/types';
import { InsightsBookmarkParams, InsightsLegend } from 'common/types/reports/bookmark';
import { ThemesResource } from 'common/report/model-resources/themes';
import { getDisplayOptions } from 'common/report/insights/util';
import { isFlattenedData } from 'common/report/insights/util/chart';
import './index.styl';
type ChartRootJsxProps = {
  params?: {
    /** flows report includes chartType */
    bookmarkParams?: InsightsBookmarkParams & {
      chartType: string;
    };
    onBookmarkUpdate?: (args: {
      params: InsightsBookmarkParams;
      requery: boolean;
      saveBoardOverrides?: boolean;
    }) => void;
  };
};

/**
 * This component serves as the context provider for all chart types.
 *
 * It is important that every chart creates a new instance of `SegmentFilterController`
 * since legend & table data are local to each chart. SegmentFilterController must never
 * be initialized at the root level.
 *
 * @todo remove `segmentFilterController` from IronContextRegistry so that the above requirement
 * is type enforced
 */

export class ChartRoot extends ParamComponent<ChartRootJsxProps[`params`], EmptyObj, IronContextRegistry> {
  constructor() {
    super(...arguments);
    _defineProperty(this, "resourceCustomSubscriptions", new Map([[ThemesResource, () => {
      this.getContext("segmentFilterController").updateConnectedComponents();
      this.update();
    }]]));
    /**
     * Legend interactions that update bookmarks:
     * - Color assignment
     * - Legend item selection changes
     */
    _defineProperty(this, "handleLegendChange", () => {
      var _onBookmarkUpdate, _ref;
      const bookmarkParams = this.params!.bookmarkParams;
      if (!bookmarkParams) {
        return;
      }
      // Flows charts don't call segmentFilterController#loadSections
      const isFlows = (bookmarkParams === null || bookmarkParams === void 0 ? void 0 : bookmarkParams.chartType) === "sankey";
      if (!this.segmentFilterController.hasLoaded() && !isFlows) {
        return;
      }
      let legend: InsightsLegend = {};
      if (isFlows) {
        var _bookmarkParams$legen;
        // Flows charts don't have a selectable legend; params.legend is currently only used for color assignment
        // (params.legend.overrides). Resetting the legend on breakdown change is handled by
        // FlowsQueryBuilder#bookmarkUpdateFromQbUpdate
        const existingOverrides = bookmarkParams === null || bookmarkParams === void 0 || (_bookmarkParams$legen = bookmarkParams.legend) === null || _bookmarkParams$legen === void 0 ? void 0 : _bookmarkParams$legen.overrides;
        legend = {
          overrides: _objectSpread(_objectSpread({}, existingOverrides ? existingOverrides : {}), this.segmentFilterController.localOverrides)
        };
      } else {
        const savedLegend = this.segmentFilterController.toBookmarkSavedLegend();
        const chartType = getDisplayOptions(
        // TODO - go/j/AE-796: Replace these with Pydantic-generated counterparts once fully defined and remove typecasts
        bookmarkParams.sections as InsightsBookmarkSections, bookmarkParams.displayOptions as DisplayOptions).chartType;
        const isFlat = isFlattenedData(chartType);
        legend = {
          overrides: this.segmentFilterController.localOverrides
        };
        if (isFlat) {
          legend.individualSelectedSegments = savedLegend[0];
        } else {
          legend.filteredBreakdownSegments = savedLegend;
        }
      }
      const legendOverrides = this.getContext("themes").legendOverrides || {};
      (_onBookmarkUpdate = (_ref = this.params!).onBookmarkUpdate) === null || _onBookmarkUpdate === void 0 || _onBookmarkUpdate.call(_ref, {
        params: _objectSpread(_objectSpread({}, bookmarkParams), {}, {
          legend
        }),
        // applies legend overrides for the whole board
        saveBoardOverrides: Object.keys(legendOverrides).length > 0,
        requery: false
      });
    });
  }
  get config() {
    return {
      template: () => <div />,
      contexts: ["segmentFilterController" as const, "currentProject" as const, "themes" as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController()
      },
      params: {
        bookmarkParams: Object,
        onBookmarkUpdate: Function
      },
      hooks: {
        preUpdate: (_newState: any, newParams?: ChartRootJsxProps[`params`]) => {
          if (!newParams) {
            return;
          }
          if (newParams.bookmarkParams) {
            // this does not currently run in download mode (?download=1), so it is called in connectedCallback too
            this.setThemeAndSegmentColorOverrides(newParams.bookmarkParams);

            /** makes sure legend selection changes to "Top N" when segments change */
            if (this.params!.bookmarkParams && this.shouldResetSegmentFilterSelectMode(this.params!.bookmarkParams, newParams.bookmarkParams)) {
              this.segmentFilterController.selectMode = SelectMode.Position;
            }
          }
        }
      }
    };
  }
  connectedCallback(): void {
    var _this$params;
    this.segmentFilterController.themesResource = this.getContext("themes");
    this.segmentFilterController.handleLegendChanges = this.handleLegendChange;
    super.connectedCallback();
    if ((_this$params = this.params) !== null && _this$params !== void 0 && _this$params.bookmarkParams) {
      // this was needed in download mode (?download=1) because the preUpdate hook is never run
      this.setThemeAndSegmentColorOverrides(this.params.bookmarkParams);
    }
  }
  get segmentFilterController() {
    return this.getContext("segmentFilterController");
  }
  setThemeAndSegmentColorOverrides(bookmarkParams: InsightsBookmarkParams) {
    var _bookmarkParams$displ, _bookmarkParams$displ2, _bookmarkParams$legen2;
    this.segmentFilterController.setReportTheme((_bookmarkParams$displ = (_bookmarkParams$displ2 = bookmarkParams.displayOptions) === null || _bookmarkParams$displ2 === void 0 ? void 0 : _bookmarkParams$displ2.theme) !== null && _bookmarkParams$displ !== void 0 ? _bookmarkParams$displ : null);
    this.segmentFilterController.localOverrides = (_bookmarkParams$legen2 = bookmarkParams.legend) === null || _bookmarkParams$legen2 === void 0 ? void 0 : _bookmarkParams$legen2.overrides;
  }
  shouldResetSegmentFilterSelectMode(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    var _original$sections, _original$sections2, _edited$sections, _edited$sections2;
    if ((_original$sections = original.sections) !== null && _original$sections !== void 0 && _original$sections.cohorts && (_original$sections2 = original.sections) !== null && _original$sections2 !== void 0 && _original$sections2.group && edited !== null && edited !== void 0 && (_edited$sections = edited.sections) !== null && _edited$sections !== void 0 && _edited$sections.cohorts && edited !== null && edited !== void 0 && (_edited$sections2 = edited.sections) !== null && _edited$sections2 !== void 0 && _edited$sections2.group) {
      const cohortsChanged = !isEqual(original.sections.cohorts, edited.sections.cohorts);
      const groupsChanged = !isEqual(original.sections.group, edited.sections.group);
      return cohortsChanged || groupsChanged;
    }
    return false;
  }
}
export const ChartRootEl = defineAndMakeJsxFactory<ChartRootJsxProps>("mp-chart-root", ChartRoot);
export const ChartRootReact = panel2react<ChartRootJsxProps>("mp-chart-root", ChartRoot);